import {
	FeatureServiceBinder,
	FeatureServiceProviderDefinition,
	FeatureServices,
	SharedFeatureService,
} from '@feature-hub/core';
import { AuthServiceProviderV2, IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { IProfileServiceV2, ProfileServiceV2Impl, UserService } from '@vwdfive/myvw-services';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { Bonus, PersonBonus } from './models/bonus';
import { getMyBonus } from './api/bonus-api';
import { acceptPerson, consentsPerson, createPerson, getPerson, unsuscribePerson } from './api/person-api';
import { confirmVehicle, createVehicle, deleteVehicle, getVehicles } from './api/vehicles-api';
import { IEsOnehubVWConfig } from './models/es-one-hub-config';
import { GetPersonUser, IdentityKitUser, PortalPersonStatus } from './models/portal';
import { ApiResponse } from './models/api-response';
import { IdentityKitCompleteMatchForm } from './models/users';
import { DataVehicles, Vehicle } from './models/vehicles';
import { PortalServiceV1 } from './v1';
import { getDataCollection, newAnswerDataCollection } from './api/data-collection-api';
import { DataCollection } from './models/dataCollection';
import { getMaintenanceDetail, getMaintenanceNext, getMaintenanceVehicle } from './api/maintenance-plan-api';
import { MaintenanceDetail, MaintenanceNext, MaintenanceVehicle } from './models/maintenancePlan';
import { participatePromotion } from './api/promotions-api';

export interface SharedIdentityMatchFeatureService extends SharedFeatureService {
	readonly '1.0.0': FeatureServiceBinder<PortalServiceV1>;
}

export enum IdentityMatchStatus {
	OK = 'OK',
	PENDING = 'PENDING',
}

export interface LayerDependencies extends FeatureServices {
	readonly 'vw:authService': AuthServiceProviderV2;
	readonly 'service-config-provider': ServiceConfigProviderV1;
}

export const portalFeatureServiceDefinition: FeatureServiceProviderDefinition<SharedIdentityMatchFeatureService> = {
	//id: 'portal-feature-service',
	id: 'es:portal-feature-service',
	dependencies: {
		featureServices: {
			'service-config-provider': '1.0.0',
		},
	},
	optionalDependencies: {
		featureServices: {
			['vw:authService']: '2.0.0',
		},
	},

	create: ({ featureServices }) => {
		const serviceConfigProvider = featureServices['service-config-provider'] as ServiceConfigProviderV1;
		const authServiceBuilder = featureServices['vw:authService'] as AuthServiceProviderV2 | undefined;
		const authServiceMarketFlag: IAuthServiceV2 | null = authServiceBuilder?.register('market') || null;

		const profileService =
			authServiceMarketFlag && serviceConfigProvider.configs['customer-profile']
				? new ProfileServiceV2Impl(authServiceMarketFlag, serviceConfigProvider.configs['customer-profile'].urlOrigin)
				: null;
		const userService = authServiceMarketFlag ? new UserService(authServiceMarketFlag) : null;

		const esOnehubVWConfig = serviceConfigProvider.configs['es-onehub-vw']?.customConfig as IEsOnehubVWConfig;

		const personInfo: BehaviorSubject<GetPersonUser | null> = new BehaviorSubject<GetPersonUser | null>(null);

		const userIdentityKitLogged: BehaviorSubject<IdentityKitUser> = new BehaviorSubject<IdentityKitUser>({
			identityKitId: null,
			email: null,
			firstName: null,
			lastName: null,
			salutation: null,
			academicTitle: null,
			phoneNumber: null,
			phoneNumbers: null,
		} as IdentityKitUser);

		const needsLogin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
		const validatingIDKit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

		const personVehicles: BehaviorSubject<DataVehicles | null> = new BehaviorSubject<DataVehicles | null>(null);

		const personDataCollection: BehaviorSubject<DataCollection | null> = new BehaviorSubject<DataCollection | null>(
			null,
		);

		///////////////////
		//Variables bonos//
		///////////////////
		const personBonus: BehaviorSubject<PersonBonus | null> = new BehaviorSubject<PersonBonus | null>(null);

		const portalFeatureService_1_0_0 = {
			featureService: {
				//Gestion de Login
				getPerson: (): BehaviorSubject<GetPersonUser | null> => {
					if (!authServiceMarketFlag || !profileService || !userService) {
						return personInfo;
					}

					if (!personInfo.getValue()) {
						portalFeatureService_1_0_0.featureService
							.isAuthenticated(authServiceMarketFlag, profileService, userService)
							.then((identityKit) => {
								if (identityKit && identityKit.identityKitId && authServiceMarketFlag) {
									getPerson(authServiceMarketFlag, esOnehubVWConfig)
										.then((response) => {
											personInfo.next({
												identityKitUser: identityKit,
												portalUser: response as ApiResponse<PortalPersonStatus>,
											});
											console.log('🚀 ~ .then ~ personInfo', personInfo.getValue());
										})
										.catch((err) => {
											personInfo.error(err);
										});
								}
							});
					}

					return personInfo;
				},

				isAuthenticated: async (
					authServiceInstance: IAuthServiceV2 | null,
					profileService: IProfileServiceV2 | null,
					userService: UserService | null,
				): Promise<IdentityKitUser | undefined> => {
					if (!authServiceInstance || !profileService || !userService) {
						return undefined;
					}

					validatingIDKit.next(true);
					const logged = await authServiceInstance.isAuthenticated();
					if (logged) {
						needsLogin.next(false);
						const userProfile = await profileService.getUserProfile();
						const email = await userService.getCurrentUserEmail();
						const identityKitId = await userService.getCurrentUserId();

						const identityKitUser = {
							...userProfile,
							email: email,
							identityKitId: identityKitId,
						} as IdentityKitUser;

						userIdentityKitLogged.next(identityKitUser);
						validatingIDKit.next(false);

						console.log('🚀 ~ App ~ identityKitUser', identityKitUser);
						return identityKitUser;
					} else {
						validatingIDKit.next(false);
						needsLogin.next(true);
					}
					return;
				},

				isValidatingIDKit: (): BehaviorSubject<boolean> => validatingIDKit,
				hasToLog: (): BehaviorSubject<boolean> => needsLogin,

				createPerson: (
					formValues: IdentityKitCompleteMatchForm,
					perfiladoComercial: string,
					device: string,
					tc: string,
					url: string,
				): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						createPerson(authServiceMarketFlag, esOnehubVWConfig, {
							personalData: {
								name: formValues?.name || '',
								firstSurname: formValues?.surname1 || '',
								secondSurname: formValues?.surname2 || '',
								documentType: formValues?.typeDocument || '',
								documentValue: formValues?.document || '',
								email: personInfo.getValue()?.identityKitUser.email as string,
								//identitykitId: personInfo.getValue()?.identityKitUser.identityKitId as string,
							},
							clubVWData: {
								perfiladoComercial: perfiladoComercial,
								originDeviceType: device,
								originTrackingCode: tc,
								formUrl: url,
							},
						})
							.then((createdPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: createdPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								console.log('error', error);
								personInfo.next(null);
								throw error;
							}),
					);
				},

				acceptPerson: (
					perfiladoComercial: string,
					device: string,
					tc: string,
					url: string,
				): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						acceptPerson(authServiceMarketFlag, esOnehubVWConfig, {
							clubVWData: {
								perfiladoComercial: perfiladoComercial,
								originDeviceType: device,
								originTrackingCode: tc,
								formUrl: url,
							},
						})
							.then((acceptedPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: acceptedPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								console.log(error);
								personInfo.next(null);
								throw error;
							}),
					);
				},

				//Consentimietos de la persona
				consentsPerson: (notificaciones: boolean): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						consentsPerson(authServiceMarketFlag, esOnehubVWConfig, {
							notificaciones: notificaciones,
						})
							.then((consentsPersonData) => {
								const currentPerson = personInfo.getValue();
								if (currentPerson) {
									const updated = {
										identityKitUser: currentPerson.identityKitUser,
										portalUser: consentsPersonData,
									};
									personInfo.next(updated);
									return updated;
								} else {
									return null;
								}
							})
							.catch((error) => {
								throw error;
							}),
					);
				},

				//Baja del club volkswagen
				unsuscribePerson: (): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						unsuscribePerson(authServiceMarketFlag, esOnehubVWConfig)
							.then((unsuscribedPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: unsuscribedPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								personInfo.next(null);
								throw error;
							}),
					);
				},

				//Gestion de vehiculos
				getPersonVehicles: (): BehaviorSubject<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return personVehicles;
					}
					if (!personVehicles.getValue() && !personVehicles.observed) {
						const identityKitUser = userIdentityKitLogged.getValue().identityKitId;

						if (identityKitUser) {
							(async (): Promise<DataVehicles> => {
								const vehicles = (await getVehicles(authServiceMarketFlag, esOnehubVWConfig))?.data;
								return vehicles;
							})()
								.then((userVehicles) => {
									if (userVehicles.orders) {
										userVehicles.vehicles = [...userVehicles.vehicles, ...userVehicles.orders];
									}
									personVehicles.next(userVehicles);
								})
								.catch((err) => {
									personVehicles.error(err);
								});
						}
					}
					return personVehicles;
				},

				getVehicleByFrame: (frame: string): BehaviorSubject<Vehicle | null> => {
					const vehicleByFrame = new BehaviorSubject<Vehicle | null>(null);

					const vehicles = personVehicles.getValue();
					if (vehicles) {
						const foundVehicle = vehicles.vehicles.find((vehicle) => vehicle.frame === frame);
						vehicleByFrame.next(foundVehicle || null);
					}

					return vehicleByFrame;
				},

				//Gestion de coches journey
				confirmPersonVehicle: (frame: string): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						confirmVehicle(authServiceMarketFlag, esOnehubVWConfig, frame).then((response) => {
							const updatedVehicles = response?.data;
							if (updatedVehicles?.orders) {
								updatedVehicles.vehicles = [...updatedVehicles.vehicles, ...updatedVehicles.orders];
							}
							personVehicles.next(updatedVehicles);
							return updatedVehicles;
						}),
					);
				},

				deletePersonVehicle: (frame: string): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						deleteVehicle(authServiceMarketFlag, esOnehubVWConfig, frame).then((response) => {
							const updatedVehicles = response?.data;
							if (updatedVehicles?.orders) {
								updatedVehicles.vehicles = [...updatedVehicles.vehicles, ...updatedVehicles.orders];
							}
							personVehicles.next(updatedVehicles); // ← Estado global
							return updatedVehicles; // ← Lo que emite from()
						}),
					);
				},

				createPersonVehicle: (
					frame: string,
					license: string,
					fichaTecnica: File,
					permisoCirculacion: File,
				): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						createVehicle(
							authServiceMarketFlag,
							esOnehubVWConfig,
							frame,
							license,
							fichaTecnica,
							permisoCirculacion,
						).then((response) => {
							const createdVehicle = response?.data;
							if (createdVehicle?.orders) {
								createdVehicle.vehicles = [...createdVehicle.vehicles, ...createdVehicle.orders];
							}
							personVehicles.next(createdVehicle); // ← Estado global
							return createdVehicle; // ← Lo que emite el Observable
						}),
					);
				},

				//Gestion de Data collection
				getPersonDataCollection: (): BehaviorSubject<DataCollection | null> => {
					if (!authServiceMarketFlag) {
						return personDataCollection;
					}
					if (!personDataCollection.getValue() && !personDataCollection.observed) {
						const identityKitUser = userIdentityKitLogged.getValue().identityKitId;

						if (identityKitUser) {
							(async (): Promise<DataCollection> => {
								const dataCollection = (await getDataCollection(authServiceMarketFlag, esOnehubVWConfig))?.data;
								return dataCollection;
							})().then((data) => {
								personDataCollection.next(data);
							});
						}
					}
					return personDataCollection;
				},

				//Gestion de new answer Data collection
				newPersonAnswerDataCollection: (questionAnswer: string[]): Observable<DataCollection | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						newAnswerDataCollection(authServiceMarketFlag, esOnehubVWConfig, questionAnswer).then((response) => {
							const updatedDataCollection = response?.data;
							personDataCollection.next(updatedDataCollection);
							return updatedDataCollection;
						}),
					);
				},

				////////////////////////////
				//Llamadas mantenimientos//
				////////////////////////////
				getMaintenanceVehicle: async (frame: string): Promise<MaintenanceVehicle> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceVehicle = (await getMaintenanceVehicle(authServiceMarketFlag, esOnehubVWConfig, frame))
						?.data;
					return maintenanceVehicle;
				},

				getMaintenanceNext: async (frame: string): Promise<MaintenanceNext> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceNext = (await getMaintenanceNext(authServiceMarketFlag, esOnehubVWConfig, frame))?.data;
					return maintenanceNext;
				},

				getMaintenanceDetail: async (frame: string, orCode: string): Promise<MaintenanceDetail> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceDetail = (await getMaintenanceDetail(authServiceMarketFlag, esOnehubVWConfig, frame, orCode))
						?.data;
					return maintenanceDetail;
				},

				//////////////////
				//Llamadas Bonos//
				//////////////////
				getPersonBonus: (): BehaviorSubject<PersonBonus | null> => {
					if (!authServiceMarketFlag) {
						return personBonus;
					}
					if (!personBonus.getValue()) {
						(async (): Promise<PersonBonus> => {
							return (await getMyBonus(authServiceMarketFlag, esOnehubVWConfig))?.data;
						})().then((bonus) => {
							personBonus.next(bonus);
						});
					}
					return personBonus;
				},

				getBonusDetail: async (id: string): Promise<Bonus | null> => {
					if (!authServiceMarketFlag) {
						return null;
					}
					let personBonusValue = personBonus.getValue();

					if (!personBonusValue) {
						const myBonus: PersonBonus = (await getMyBonus(authServiceMarketFlag, esOnehubVWConfig))?.data;
						return myBonus.bonus.find((bonus) => bonus.id === id) ?? null;
					}

					return personBonusValue.bonus.find((bonus) => bonus.id === id) ?? null;
				},

				///////////////////////
				//Llamadas promotions//
				///////////////////////
				postParticipate: (promotionData: any): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						participatePromotion(authServiceMarketFlag, esOnehubVWConfig, promotionData).then((apiResponse) => {
							const currentPerson = personInfo.getValue();
							if (currentPerson) {
								const updated = {
									identityKitUser: currentPerson.identityKitUser,
									portalUser: apiResponse,
								};
								personInfo.next(updated);
								return updated;
							} else {
								return null;
							}
						}),
					);
				},
			},
		};
		return {
			'1.0.0': () => portalFeatureService_1_0_0,
		};
	},
};
